import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatCLP, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { CreditCard, CircleNotch, CheckCircle, ShieldCheck, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, refresh } = useCart();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [card, setCard] = useState({ name: "", number: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        coupon: coupon || null, card_name: card.name, card_number: card.number,
      });
      await refresh();
      setDone(data);
      toast.success("¡Pago aprobado!");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const payWebpay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/webpay/init", { coupon: coupon || null });
      window.location.href = data.url + "?token_ws=" + data.token;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto px-6 py-16 text-center animate-fade-up" data-testid="checkout-success">
          <CheckCircle size={64} weight="fill" className="text-primary mx-auto mb-6" />
          <h1 className="font-display text-3xl font-800 tracking-tight">¡Compra confirmada!</h1>
          <p className="text-muted-foreground mt-3">
            Orden <span className="font-mono">{done.id.slice(0, 8).toUpperCase()}</span> · {formatCLP(done.total)}
          </p>
          <p className="text-sm text-muted-foreground mt-1">Ya tienes acceso a tus cursos.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button onClick={() => navigate("/dashboard/cursos")} className="bg-primary text-white hover:bg-blue-600">
              Ir a Mis Cursos
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard/compras")}>Ver compra</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-800 tracking-tight mb-8">Finalizar compra</h1>
        {cart.items.length === 0 ? (
          <p className="text-muted-foreground">Tu carrito está vacío. <button onClick={() => navigate("/catalogo")} className="text-primary underline">Explorar cursos</button></p>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <form onSubmit={pay} className="lg:col-span-2 space-y-6">
              <div className="bg-white border border-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={20} className="text-primary" />
                  <h2 className="font-display font-800 text-lg">Datos de pago</h2>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary rounded-md px-3 py-2 mb-5">
                  <ShieldCheck size={15} /> Paga con <b className="text-foreground">Webpay Plus</b> (Transbank). El pago simulado queda como alternativa de demo.
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cn">Nombre en la tarjeta</Label>
                    <Input id="cn" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })}
                      required data-testid="checkout-card-name" className="mt-1.5" placeholder="Juan Pérez" />
                  </div>
                  <div>
                    <Label htmlFor="cnum">Número de tarjeta</Label>
                    <Input id="cnum" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })}
                      required data-testid="checkout-card-number" className="mt-1.5" placeholder="4111 1111 1111 1111" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Tag size={20} className="text-primary" />
                  <h2 className="font-display font-800 text-lg">Cupón de descuento</h2>
                </div>
                <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  data-testid="checkout-coupon" placeholder="Ej: BIENVENIDA20" />
                <p className="text-xs text-muted-foreground mt-2">Prueba <b>BIENVENIDA20</b> para 20% de descuento.</p>
              </div>

              <Button type="button" onClick={payWebpay} disabled={loading} data-testid="checkout-webpay-btn"
                className="w-full bg-primary hover:bg-blue-600 text-white h-12 text-base gap-2">
                {loading ? <CircleNotch className="animate-spin" /> : <>Pagar con Webpay {formatCLP(cart.total)}</>}
              </Button>
              <div className="flex items-center gap-3 my-1">
                <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">o</span><div className="h-px bg-border flex-1" />
              </div>
              <Button type="submit" disabled={loading} data-testid="checkout-pay-btn" variant="outline"
                className="w-full h-11 gap-2">
                Pago simulado (demo)
              </Button>
            </form>

            <div className="lg:col-span-1">
              <div className="bg-white border border-border rounded-lg p-6 sticky top-24">
                <h2 className="font-display font-800 text-lg mb-4">Tu orden</h2>
                <div className="space-y-3">
                  {cart.items.map((it) => (
                    <div key={it.course_id} className="flex justify-between text-sm gap-2">
                      <span className="text-muted-foreground line-clamp-1">{it.title}</span>
                      <span className="shrink-0">{formatCLP(it.price)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-display font-800 text-xl mt-4 pt-4 border-t border-border">
                  <span>Total</span><span>{formatCLP(cart.total)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
